import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export const useOrganizationStore = create((set, get) => ({
  organizationId: null,

  // Members
  members: [],
  loadingMembers: false,
  memberError: null,

  // Invitations
  pendingInvitations: [],
  loadingInvitations: false,

  // ── Org ─────────────────────────────────────────────────────────────────────

  // Reset all data when switching orgs
  setOrganizationId: (orgId) => {
    if (get().organizationId !== orgId) {
      set({
        organizationId: orgId,
        members: [],
        loadingMembers: false,
        memberError: null,
        pendingInvitations: [],
        loadingInvitations: false,
      });
    }
  },

  // ── Members ──────────────────────────────────────────────────────────────────

  setMembers: (members) => set({ members }),
  setLoadingMembers: (loading) => set({ loadingMembers: loading }),
  setMemberError: (error) => set({ memberError: error }),

  removeMember: (userId) =>
    set((state) => ({
      members: state.members.filter((m) => m.userId !== userId),
    })),

  updateMember: (userId, updates) =>
    set((state) => ({
      members: state.members.map((m) =>
        m.userId === userId ? { ...m, ...updates } : m
      ),
    })),

  // ── Invitations ───────────────────────────────────────────────────────────────

  setPendingInvitations: (invitations) => set({ pendingInvitations: invitations }),
  setLoadingInvitations: (loading) => set({ loadingInvitations: loading }),

  addInvitation: (invitation) =>
    set((state) => ({
      pendingInvitations: [...state.pendingInvitations, invitation],
    })),

  removeInvitation: (invitationId) =>
    set((state) => ({
      pendingInvitations: state.pendingInvitations.filter(
        (inv) =>
          inv.invitationId !== invitationId &&
          inv.token !== invitationId &&
          inv.id !== invitationId
      ),
    })),

  // Used when a member is deleted — clears any pending invites for their email
  removeInvitationsByEmail: (email) =>
    set((state) => ({
      pendingInvitations: state.pendingInvitations.filter(
        (inv) => inv.email?.toLowerCase() !== email?.toLowerCase()
      ),
    })),

  // ── Reset (called on logout) ──────────────────────────────────────────────────

  reset: () =>
    set({
      organizationId: null,
      members: [],
      loadingMembers: false,
      memberError: null,
      pendingInvitations: [],
      loadingInvitations: false,
    }),
}));

// ── Selector hooks ─────────────────────────────────────────────────────────────

export const useOrganizationMembers = () =>
  useOrganizationStore(
    useShallow((state) => ({
      members: state.members,
      loadingMembers: state.loadingMembers,
      memberError: state.memberError,
    }))
  );

export const useOrganizationInvitations = () =>
  useOrganizationStore(
    useShallow((state) => ({
      pendingInvitations: state.pendingInvitations,
      loadingInvitations: state.loadingInvitations,
    }))
  );
